import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Pagination } from "../../components/ui/Pagination";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { TableColumnHeader } from "../../components/ui/TableColumnHeader";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { themesApi } from "../../utils/api";
import { ThemeEventsModal, EventCountButton } from "./ThemeEventsModal";

const PAGE_SIZE = 50;

// Keys are what `GET /admin/themes?origin=` accepts; the same labels name the filter options
// and each row's badge.
const ORIGIN_LABELS = { ai: "Classifier", manual: "Human" };
const ORIGIN_OPTIONS = [
  { value: "", label: "All" },
  ...Object.entries(ORIGIN_LABELS).map(([value, label]) => ({ value, label })),
];

/** The whole theme catalog, searchable — what the classifier has actually produced. */
export function VocabularyView({ onError, refreshKey }) {
  // `null` after a failed load: neither the last filter's rows nor "No themes match." is true then.
  const [themes, setThemes] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  // Submit-on-enter: each query is a round trip plus a usage aggregation.
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const [origin, setOrigin] = useState("");
  const [loading, setLoading] = useState(false);
  const [showingEventsFor, setShowingEventsFor] = useState(null);

  useEffect(() => {
    // Responses can land out of order when the filter changes quickly; only the latest request
    // may write, or the table shows one filter's rows under another's header.
    let cancelled = false;
    setLoading(true);
    themesApi
      .getAll({
        page,
        page_size: PAGE_SIZE,
        ...(search && { search }),
        ...(origin && { origin }),
      })
      .then((data) => {
        if (cancelled) return;
        setThemes(data.themes || []);
        setPagination(data.pagination || null);
        onError("list", null);
      })
      .catch((e) => {
        if (cancelled) return;
        setThemes(null);
        setPagination(null);
        onError("list", e.response?.data?.detail || "Failed to load themes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search, origin, refreshKey, onError]);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent>
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={(e) => {
              submitSearch(e);
              setPage(1);
            }}
            onClear={
              search
                ? () => {
                    clearSearch();
                    setPage(1);
                  }
                : undefined
            }
            placeholder="Search themes by name..."
            inputClassName="sm:w-64"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Theme</TableHead>
                  <TableColumnHeader
                    label="Origin"
                    filter={{
                      value: origin,
                      options: ORIGIN_OPTIONS,
                      onChange: (value) => {
                        setOrigin(value);
                        setPage(1);
                      },
                    }}
                  />
                  <TableHead className="text-right">Events</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!themes?.length ? (
                  // Inside the table, never in place of it: replacing it would take the filter
                  // that caused the empty result with it.
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                      {themes ? "No themes match." : "Could not load themes."}
                    </TableCell>
                  </TableRow>
                ) : (
                  themes.map((theme) => (
                    <TableRow key={theme.id}>
                      <TableCell className="font-medium">{theme.name || theme.id}</TableCell>
                      <TableCell>
                        <Badge variant={theme.origin === "manual" ? "default" : "secondary"}>
                          {ORIGIN_LABELS[theme.origin] ?? ORIGIN_LABELS.ai}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <EventCountButton
                          usage={theme.usage}
                          onOpen={() => setShowingEventsFor(theme)}
                          label={`Show the events tagged ${theme.name || theme.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
      )}

      <ThemeEventsModal
        theme={showingEventsFor}
        onClose={() => setShowingEventsFor(null)}
      />
    </div>
  );
}
